import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { NavButton } from '../layout/SectionNav.jsx';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';

const CameraError = () => {
	return (
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			fontSize: '18px',
			color: 'rgba(255, 0, 0, .75)',
		}}>
			<p role="alert" aria-live="assertive" style={{
				textAlign: 'center',
				maxWidth: '75%',
			}}>
				We cannot access the camera, please check if the camera is being used
				elsewhere and eliminate it there. Then please retry here.
			</p>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 24 24"
				style={{ width: '128px' }}
				fill="currentColor"
			>
				<path d="m9.56 8-2-2-4.15-4.14L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.55-.18L19.73 21l1.41-1.41-8.86-8.86zM5 16V8h1.73l8 8zm10-8v2.61l6 6V6.5l-4 4V7c0-.55-.45-1-1-1h-5.61l2 2z" />
			</svg>
		</div >
	);
};


export const DocumentCamera = ({ title, cameraIncludes, cameraRotationAngle, cameraZoom = 1, onClose, onImage, onLoading }) => {
	const [cameraLoading, setCameraLoading] = useState(true);
	const [cameraAttachmentFailed, setCameraAttachmentFailed] = useState(false);
	const [isDialogOpen, setIsDialogOpen] = useState(true);
	const [availableCameras, setAvailableCameras] = useState([]);
	const [activeCameraIndex, setActiveCameraIndex] = useState(0);
	const [rotationScale, setRotationScale] = useState(1);
	const videoRef = useRef();
	const videoContainerRef = useRef();
	const canvasRef = useRef();
	const streamRef = useRef();
	const webcamErrorTimerRef = useRef(null);

	useLayoutEffect(() => {
		if (!cameraRotationAngle || cameraRotationAngle % 180 === 0) {
			setRotationScale(1);
			return;
		}
		const container = videoContainerRef.current;
		if (!container || cameraLoading || cameraAttachmentFailed) return;

		const { width, height } = container.getBoundingClientRect();
		if (!width || !height) return;

		setRotationScale(Math.min(width / height, height / width));
	}, [cameraRotationAngle, cameraLoading, cameraAttachmentFailed]);

	const convertCanvasToBlob = (canvas) => new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				reject();
				return;
			}
			resolve(blob);
		},
		'image/png',
		);
	});

	const appendWebcamToVideoElement = async (node) => {
		try {
			const preDevices = await navigator.mediaDevices.enumerateDevices();
			const device = cameraIncludes
				? preDevices.find(d => d.kind === 'videoinput' && d.label.includes(cameraIncludes))
				: undefined;

			let userMediaConstraints;
			if (device) {
				userMediaConstraints = {
					audio: false,
					video: {
						deviceId: device.deviceId,
						width: { ideal: 3840 },
						height: { ideal: 2160 },
					},
				};
			} else {
				userMediaConstraints = {
					audio: false,
					video: {
						facingMode: 'environment',
						width: { ideal: 3840 },
						height: { ideal: 2160 },
					},
				};
			}

			streamRef.current = await navigator.mediaDevices.getUserMedia(userMediaConstraints);
			const tracks = streamRef.current.getVideoTracks();

			const postDevices = await navigator.mediaDevices.enumerateDevices();
			const videoCameras = postDevices.filter(d => d.kind === 'videoinput');
			setAvailableCameras(videoCameras);

			if (tracks[0]) {
				const activeDeviceId = tracks[0].getSettings().deviceId;
				const activeIndex = videoCameras.findIndex(d => d.deviceId === activeDeviceId);
				if (activeIndex !== -1) {
					setActiveCameraIndex(activeIndex);
				}
			}

			if (webcamErrorTimerRef.current) {
				clearInterval(webcamErrorTimerRef.current);
			}

			node.srcObject = streamRef.current;
			setCameraAttachmentFailed(false);
		} catch (error) {
			setCameraAttachmentFailed(true);
			console.error(error);
		}
		setCameraLoading(false);
	};

	const initialiseWebcam = useCallback(
		async (node) => {
			if (node) {
				await appendWebcamToVideoElement(node);
			}
		}, []);

	const handleVideoRef = useCallback(
		(node) => {
			videoRef.current = node;
			if (node) {
				initialiseWebcam(node);
			}
		}, [initialiseWebcam]);

	useEffect(() => {
		return () => {
			if (streamRef.current) {
				streamRef.current.getTracks().forEach(track => track.stop());
			}
		};
	}, [streamRef]);

	useEffect(() => {
		if (
			videoRef.current &&
			cameraAttachmentFailed &&
			webcamErrorTimerRef.current === null
		) {
			webcamErrorTimerRef.current = setInterval(() => {
				initialiseWebcam(videoRef.current);
			}, 3000);
		}

		return () => {
			if (webcamErrorTimerRef.current) {
				clearInterval(webcamErrorTimerRef.current);
			}
		};
	}, [cameraAttachmentFailed, initialiseWebcam]);

	const handleCapturePicture = useCallback(async () => {
		setIsDialogOpen(false);
		onLoading?.();
		const video = videoRef.current;
		const canvas = canvasRef.current;

		if (!video || !canvas) {
			return;
		}

		const ctx = canvas.getContext('2d');
		if (ctx) {
			let canvasToConvert = canvas;
			
			// If using software zoom, we need to crop the center portion
			if (cameraZoom !== 1) {
				// Calculate the cropped region (center portion)
				const sourceWidth = video.videoWidth / cameraZoom;
				const sourceHeight = video.videoHeight / cameraZoom;
				const sourceX = (video.videoWidth - sourceWidth) / 2;
				const sourceY = (video.videoHeight - sourceHeight) / 2;
				
				// Set canvas to the cropped size
				canvas.width = sourceWidth;
				canvas.height = sourceHeight;
				
				// Draw the cropped center portion
				ctx.drawImage(
					video,
					sourceX, sourceY, sourceWidth, sourceHeight,
					0, 0, sourceWidth, sourceHeight,
				);
			} else {
				// No zoom, capture the full video
				canvas.width = video.videoWidth;
				canvas.height = video.videoHeight;
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
			}

			const blob = await convertCanvasToBlob(canvasToConvert);
			onImage({ target: { files: [blob] } });
		}
		onClose();
	}, [canvasRef, videoRef, cameraZoom]);

	const switchCamera = useCallback(async () => {
		if (availableCameras.length < 2) return;
		const nextIndex = (activeCameraIndex + 1) % availableCameras.length;
		const nextDevice = availableCameras[nextIndex];
		try {
			const newStream = await navigator.mediaDevices.getUserMedia({
				audio: false,
				video: {
					deviceId: { exact: nextDevice.deviceId },
					width: { ideal: 3840 },
					height: { ideal: 2160 },
				},
			});
			if (streamRef.current) {
				streamRef.current.getTracks().forEach(track => track.stop());
			}
			streamRef.current = newStream;
			videoRef.current.srcObject = newStream;
			setActiveCameraIndex(nextIndex);
		} catch (error) {
			console.error('Failed to switch camera:', error);
		}
	}, [availableCameras, activeCameraIndex]);



	return (
		<Dialog open={isDialogOpen} maxWidth={false}
			PaperProps={{
				sx: {
					width: '95vw',
					height: '95vh',
					maxHeight: '95vh',
					margin: 0,
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			{
				!cameraLoading && !cameraAttachmentFailed &&
				<DialogTitle sx={{
					textAlign: 'center',
					padding: '10px 20px',
				}}>
					{title}
				</DialogTitle>
			}
			<DialogContent sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				overflow: 'hidden',
				padding: cameraLoading ? '20px 8px' : '0px 8px',
				flex: 1,
				minHeight: 0,
			}}>
				{cameraLoading && <CircularProgress size={60} />}
				{cameraAttachmentFailed && <CameraError />}
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						width: '100%',
						flex: 1,
						minHeight: 0,
						overflow: 'hidden',
						position: 'relative',
					}}
					ref={videoContainerRef}
				>
					<video
						ref={handleVideoRef}
						autoPlay
						playsInline
						muted
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'contain',
							borderRadius: '7px',
							display: cameraLoading || cameraAttachmentFailed ? 'none' : 'block',
							transform: [
								cameraRotationAngle ? `rotate(${cameraRotationAngle}deg)` : '',
								rotationScale !== 1 ? `scale(${rotationScale})` : '',
								cameraZoom !== 1 ? `scale(${cameraZoom})` : '',
							].filter(Boolean).join(' ') || 'none',
						}}
					/>
					{availableCameras.length > 1 && !cameraLoading && !cameraAttachmentFailed && (
						<IconButton
							onClick={switchCamera}
							sx={{
								position: 'absolute',
								top: 8,
								right: 8,
								backgroundColor: 'rgba(0, 0, 0, 0.5)',
								color: 'white',
								'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
								zIndex: 1,
							}}
							size="large"
						>
							<CameraswitchIcon />
						</IconButton>
					)}
				</div>
				<canvas ref={canvasRef} style={{ display: 'none' }} />
			</DialogContent>
			{
				!cameraLoading &&
					<DialogActions sx={{ padding: '10px 20px' }}>
						<Box style={{
							display: 'flex',
							justifyContent: 'center',
							width: '100%',
						}} mt={2}>
							<ButtonGroup style={{
								maxWidth: 650,
								width: '100%',
							}}>
								<NavButton onClick={() => onClose()} beforeIcon={<ArrowBackIcon />} sx={{ maxHeight: '10vh' }} >
									Cancel
								</NavButton>

								<NavButton
									afterIcon={<ArrowForwardIcon />}
									onClick={() => handleCapturePicture()}
									color='secondary'
									disabled={cameraAttachmentFailed || cameraLoading}
									sx={{ maxHeight: '10vh' }}
								>Capture</NavButton>
							</ButtonGroup>
						</Box>
					</DialogActions>
			}
		</Dialog>
	);
};